
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import VueSimpleContextMenu from 'vue-simple-context-menu';
import Toasted from 'vue-toasted';
import VueTour from 'vue-tour';
import 'vue-tour/dist/vue-tour.css';
import { handleError } from '../../api';
import { WindowType } from '../../util';
import {
  Account,
  Board,
  Card,
  KanbanCreateBoardFromTemplateRequest,
  KanbanCreateTemplateFromBoardRequest,
  KanbanSettings,
  ListItem,
  Responsible,
  ResponsibleRequest,
  Ticket,
} from '../backend/backendapi';
import { KanbanState, SelectedItemDto } from './kanban.model';
import { KanbanTour } from './kanban.tour';
import { getAccountName, getSharedInfoBoard, isOwn, isPrivate } from './kanban.util';
import ModalContainer from './modal/modalcontainer/modalcontainer.controller';
import ChangeBoardOwnerModal from './modal/ChangeBoardOwnerModal';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });
Vue.use(VueTour as any);

@Component({
  name: 'KanbanList',
  components: {
    VueSimpleContextMenu,
    // VueTour,
    ChangeBoardOwnerModal,
    KanbanBoard: require('./board/kanbanboard.vue.html').default,
    ModalContainer: require('./modal/modalcontainer/modalcontainer.vue.html').default,
  }, methods: {},
})
export default class KanbanList extends Vue {
  //external functions, see kanbanlist.util.ts
  isOwn = isOwn;
  isPrivate = isPrivate;
  getAccountName = getAccountName;

  //props
  @Prop({ required: true })
  private readonly state!: KanbanState;

  //tour
  private readonly tour = new KanbanTour();

  //runtime
  private newBoard: string | null = null;
  private height: number | null = null;

  //board name edit
  private editBoardId: number | null = null;
  private editBoardName: string | null = null;

  //board options context menu
  private boardOptionsContextMenuOptions: any[] = [];
  private boardOptionsContextMenuOptionsFiltered: any[] = [];

  mounted(): void {
    this.$root.$on('reloaded', () => this.startTour());
    window.addEventListener('resize', this.calcHeight);
    this.calcHeight();

    let boardlocked = (board: Board): boolean => board.lockdownDelay! >= 0 && board.createdOn!.getTime() + board.lockdownDelay! * 1000 < new Date().getTime();

    //options menu options
    this.boardOptionsContextMenuOptions.push(
        { id: 'share', name: this.$t('txtShare'), canShow: (board) => !this.state.templatesView && this.isOwn(board) },
        {
          id: 'rename',
          name: this.$t('btnRename'),
          canShow: (board) => !boardlocked(board) && (this.state.canEditBoards || (this.isOwn(board) && this.isPrivate(this.state, board))),
        },
        {
          id: 'delete',
          name: this.$t('txtDelete'),
          canShow: (board) => !boardlocked(board) && (this.state.canDeleteBoards || (this.isOwn(board) && this.isPrivate(this.state, board))),
        },
        {
          id: 'loggingEnable',
          name: this.$t('kanbanLoggingEnable'),
          canShow: (board) => !this.state.templatesView && this.isOwn(board) && !board.isTransactionLogActive,
        },
        {
          id: 'loggingDisable',
          name: this.$t('kanbanLoggingDisable'),
          canShow: (board) => !this.state.templatesView && this.isOwn(board) && board.isTransactionLogActive,
        },
        {
          id: 'readConfirmationsEnable',
          name: this.$t('kanban.readConfirmation.enable'),
          canShow: (board) => !this.state.templatesView && this.isOwn(board) && !board.enableReadConfirmations,
        },
        {
          id: 'readConfirmationsDisable',
          name: this.$t('kanban.readConfirmation.disable'),
          canShow: (board) => !this.state.templatesView && this.isOwn(board) && board.enableReadConfirmations,
        },
        {
          id: 'lockdownDelay',
          name: this.$t('kanbanLockdownDelay'),
          canShow: (board) => !this.state.templatesView && !boardlocked(board) && this.isOwn(board) && board.lockdownDelay < 0,
        },
        {
          id: 'onlyTimeTrackingForOthersEnable',
          name: this.$t('kanban.onlyTimeTrackingForOthers.enable'),
          canShow: (board) => !this.state.templatesView && this.isOwn(board) && !board.onlyTimeTrackingForOthers,
        },
        {
          id: 'onlyTimeTrackingForOthersDisable',
          name: this.$t('kanban.onlyTimeTrackingForOthers.disable'),
          canShow: (board) => !this.state.templatesView && this.isOwn(board) && board.onlyTimeTrackingForOthers,
        },
        {
          id: 'onlyShowAssignedCardsToOthersEnable',
          name: this.$t('kanban.onlyShowAssignedCardsToOthers.enable'),
          canShow: (board) => !this.state.templatesView && this.isOwn(board) && !board.onlyShowAssignedCardsToOthers,
        },
        {
          id: 'onlyShowAssignedCardsToOthersDisable',
          name: this.$t('kanban.onlyShowAssignedCardsToOthers.disable'),
          canShow: (board) => !this.state.templatesView && this.isOwn(board) && board.onlyShowAssignedCardsToOthers,
        },
        {
          id: 'saveAsTemplate',
          name: this.$t('kanbanSaveAsTemplate'),
          canShow: (board) => !this.state.templatesView && !this.isInbox(board) && (this.isOwn(board) || this.state.canEditBoards),
        },
        { id: 'changeOwner', name: this.$t('kanban.board.options.changeOwner'), canShow: (board) => !this.isInbox(board) && this.isOwn(board) },
    );
  }

  beforeDestroy(): void {
    window.removeEventListener('resize', this.calcHeight);
  }

  private startTour(): void {
    if (this.state.settings != null && this.state.settings.tourInitialCounter < 1) {
      (this['$tours']['InitialTour'] as any).callbacks.onStop = this.onInitialTourStop;
      this['$tours']['InitialTour'].start();
      return;
    }
    if (this.state.settings != null && this.state.settings.tourBoardExistsCounter < 1
        && this.state.boards.length > 0) {
      this.state.selectedBoard = this.state.boards[0];
      (this['$tours']['BoardExistsTour'] as any).callbacks.onStop = this.onBoardExistsTourStop;
      this.$nextTick(() => this['$tours']['BoardExistsTour'].start());
      return;
    }
    if (this.state.settings != null && this.state.settings.tourFlowExistsCounter < 1
        && this.state.boards.some(e => (e.tickets ?? []).length > 0)) {
      this.state.selectedBoard = this.state.boards.filter(e => (e.tickets ?? []).length > 0)[0];
      //flow should be expanded
      (this['$tours']['FlowExistsTour'] as any).callbacks.onStop = this.onFlowExistsTourStop;
      this.$nextTick(() => this['$tours']['FlowExistsTour'].start());
      return;
    }
  }

  private async onInitialTourStop(): Promise<void> {
    this.state.settings!.tourInitialCounter++;
    await this.state.settingsAPI.update('KanbanSettings', false, false, JSON.stringify(this.state.settings));
    this.startTour();
  }

  private async onBoardExistsTourStop(): Promise<void> {
    this.state.settings!.tourBoardExistsCounter++;
    await this.state.settingsAPI.update('KanbanSettings', false, false, JSON.stringify(this.state.settings));
    this.startTour();
  }

  private async onFlowExistsTourStop(): Promise<void> {
    this.state.settings!.tourFlowExistsCounter++;
    await this.state.settingsAPI.update('KanbanSettings', false, false, JSON.stringify(this.state.settings));
    this.startTour();
  }

  private get title(): string {
    if (this.state.loading) {
      return '';
    }
    return this.state.accounts?.parentList?.find(e => e.dbId === this.state.accounts?.selectedItem)?.text ?? '';
  }

  private get accountsList(): Account[] {
    return this.state.accounts?.parentList?.filter(e => e.account).map(e => e.account!) ?? [];
  }

  private get boardsToDisplay(): Board[] {
    let boards = this.state.templatesView ? this.state.templates : this.state.boards;
    boards = boards.filter(board => !board.onlyTimeTrackingForOthers || isOwn(board)); //filter out time-tracking-only boards that aren't mine
    return boards;
  }

  get areMassSelectedCardsWithin1Board(): boolean {
    const cardIds = new Set(this.state.massSelectionCards.map(e => e.id));
    const boards = this.state.boards.filter(board => board.tickets?.some(flow => flow.cards?.some(card => cardIds.has(card.id))));
    return boards.length < 2;
  }

  get canRequestApproval(): boolean {
    return window.application.permissions.kanban.approval;
  }

  private click(board: Board): void {
    this.state.selectedBoard = board;
  }

  private sharedAdd(event: { user: ListItem, board: Board }): void {
    this.state.sharedBoardAPI.create(event.board.id!, event.user.id!)
        .then(_ => {
          this.$toasted.info(this.$t('txtSaved').toString());
          getSharedInfoBoard(this.state, event.board).userIds.push(event.user.id!);
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private sharedRemove(event: { user: ListItem, board: Board }): void {
    this.state.sharedBoardAPI.delete(event.board.id!, event.user.id!)
        .then(_ => {
          this.$toasted.info(this.$t('txtSaved').toString());
          const info = getSharedInfoBoard(this.state, event.board);
          const index = info.userIds.findIndex(e => e == event.user.id!);
          if (index > -1)
            info.userIds.splice(index, 1);
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private createBoard(): void {
    if (!this.newBoard)
      return;

    this.createBoardWithName(this.newBoard)
        .then(_ => {
          this.newBoard = null;
        });
  }

  private createBoardWithName(name: string): Promise<void> {
    let board = new Board();
    board.createdOn = new Date();
    board.name = name;
    board.tickets = [];
    board.userId = window.application.kanban.user!.id;
    return this.state.boardAPI.create(board)
        .then(newboard => {
          this.$toasted.info(this.$t('txtSaved').toString());
          this.state.boards.push(newboard.result!);
          this.startTour();
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private createBoardWithNameAndTemplate(name: string, templateId?: number): Promise<void> {
    if (!templateId) {
      return this.createBoardWithName(name);
    }
    let toast = this.$toasted.info(this.$t('kanbanCreatingBoardThisMayTakeAFewSeconds').toString(), { duration: 100000 });
    (this.$refs.modalContainer as ModalContainer).getBoardCreateModal()?.showLoadingSymbol();
    return this.state.templatesApi.createBoardFromTemplate(new KanbanCreateBoardFromTemplateRequest({ name, templateId }))
        .then(newboard => {
          this.$toasted.info(this.$t('txtSaved').toString());
          this.state.boards.push(newboard);
          this.newBoard = null;
          this.startTour();
          (this.$refs.modalContainer as ModalContainer).getBoardCreateModal()?.dismiss();
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e))
        .finally(() => {
          toast.goAway();
          (this.$refs.modalContainer as ModalContainer).getBoardCreateModal()?.hideLoadingSymbol();
        });
  }

  private renameBoard(board: Board): void {
    if (!this.editBoardId || !this.editBoardName) {
      return;
    }

    let board2api = board.clone();
    board2api.name = this.editBoardName;
    board2api.tickets = []; //contains refs to aspnetuser which does not (de)serialize well
    this.state.boardAPI.update(board2api)
        .then(_ => {
          this.$toasted.info(this.$t('txtSaved').toString());
          board.name = board2api.name;
          this.editBoardId = null;
          this.editBoardName = null;
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private deleteBoard(board: Board): void {
    if (confirm(this.$t('txtInfoDelete').toString())) {
      this.state.boardAPI.delete(board.id!)
          .then(_ => {
            this.$toasted.info(this.$t('txtDeleted').toString());
            let index = this.state.boards.findIndex(e => e.id === board.id);
            if (index > -1) {
              this.state.boards.splice(index, 1);
            }
            index = this.state.templates.findIndex(e => e.id === board.id);
            if (index > -1) {
              this.state.templates.splice(index, 1);
            }
          })
          .catch(e => handleError(this.$toasted, this.$i18n, e));
    }
  }

  private toggleLogging(board: Board, enable: boolean): void {
    this.state.boardAPI.updateLogging(board.id!, enable)
        .then(_ => {
          board.isTransactionLogActive = enable;
          this.$toasted.info(this.$t('txtSaved') + '. ' + (board.isTransactionLogActive ? this.$t('kanbanLoggingEnabled') : this.$t('kanbanLoggingDisabled')));
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private toggleReadConfirmations(board: Board, enable: boolean): void {
    this.state.boardAPI.updateReadConfirmations(board.id!, enable)
        .then(_ => {
          board.enableReadConfirmations = enable;
          this.$toasted.info(this.$t('txtSaved') + '. ' + (board.enableReadConfirmations ? this.$t('kanban.readConfirmation.enabled') : this.$t('kanban.readConfirmation.disabled')));
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private saveLockdownDelay(event: { board: Board, seconds: number }): void {
    this.state.boardAPI.updateLockdownDelay(event.board.id!, event.seconds)
        .then(_ => {
          event.board.lockdownDelay = event.seconds;
          this.$toasted.info(this.$t('txtSaved').toString());
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private toggleOnlyTimeTrackingForOthers(board: Board, enable: boolean): void {
    this.state.boardAPI.updateOnlyTimeTrackingForOthers(board.id!, enable)
        .then(_ => {
          board.onlyTimeTrackingForOthers = enable;
          this.$toasted.info(this.$t('txtSaved') + '. ' + (board.onlyTimeTrackingForOthers ? this.$t('kanban.onlyTimeTrackingForOthers.enabled') : this.$t('kanban.onlyTimeTrackingForOthers.disabled')));
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }
  
  private toggleOnlyShowAssignedCardsToOthers(board: Board, enable: boolean): void {
    this.state.boardAPI.updateOnlyShowAssignedCardsToOthers(board.id!, enable)
        .then(_ => {
          board.onlyShowAssignedCardsToOthers = enable;
          this.$toasted.info(this.$t('txtSaved') + '. ' + (board.onlyShowAssignedCardsToOthers ? this.$t('kanban.onlyShowAssignedCardsToOthers.enabled') : this.$t('kanban.onlyShowAssignedCardsToOthers.disabled')));
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e));
  }

  private createTemplateFromBoard(boardId: number): Promise<void> {
    let toast = this.$toasted.info(this.$t('kanbanCreatingTemplateThisMayTakeAFewSeconds').toString(), { duration: 100000 });
    return this.state.templatesApi.createTemplateFromBoard(new KanbanCreateTemplateFromBoardRequest({ boardId }))
        .then(template => {
          this.state.templates.push(template);
          this.$toasted.info(this.$t('txtSaved').toString());
        })
        .catch(e => handleError(this.$toasted, this.$i18n, e))
        .finally(() => toast.goAway());
  }

  //responsible
  private async responsibleAdd(user: ListItem): Promise<void> {
    for (let c of this.state.massSelectionCards) {
      const card = c;

      //prevent double assignments
      if (card.responsibles !== undefined && card.responsibles.findIndex(r => r.userId === user.id) > -1) {
        continue;
      }

      let r = new Responsible();
      r.user = user;
      r.userId = user.id;
      r.cardId = card.id;
      r.assignedOn = new Date();

      //save to backend
      let req = new ResponsibleRequest();
      req.cardId = card.id!;
      req.userId = user.id!;
      req.add = true;
      await this.state.cardsAPI.updateResponsible(req)
          .then(_ => {
            if (!card.responsibles)
              card.responsibles = [];
            card.responsibles.push(r);
          })
          .catch(e => handleError(this.$toasted, this.$i18n, e));
    }
    this.$toasted.info(this.$t('txtSaved').toString());
  }

  private async responsibleRemove(responsible: Responsible): Promise<void> {
    for (let c of this.state.massSelectionCards) {
      const card = c;
      let index = (card.responsibles ?? []).findIndex(e => e.userId == responsible.userId);
      if (index > -1) {
        //save to backend
        let req = new ResponsibleRequest();
        req.cardId = card.id!;
        req.userId = responsible.user!.id!;
        req.add = false;
        await this.state.cardsAPI.updateResponsible(req)
            .then(_ => {
              card.responsibles?.splice(index!, 1);
            })
            .catch(e => handleError(this.$toasted, this.$i18n, e));
      }
    }
    this.$toasted.info(this.$t('txtSaved').toString());
  }

  private get responsibles(): Responsible[] { //filters for responsibles present on every card in this.state.massSelectionCards
    if (!this.state.massSelectionCards.length) {
      return [];
    }
    let responsibles = this.state.massSelectionCards[0].responsibles ?? [];
    return responsibles.filter(r => this.state.massSelectionCards.every(c => c.responsibles?.find(cr => cr.userId === r.userId)));
  }

  //util
  private openCard(event: { board: Board, flow: Ticket, card: Card }): void {
    (this.$refs.modalContainer as ModalContainer).openDetails(event.board, event.flow, event.card);
  }

  private calcHeight(): void {
    this.height = null;
    if (this.state.settings?.layout === 'trello') {
      let bb = this.$el.getBoundingClientRect();
      this.height = window.innerHeight - bb.top;
    }
  }

  private isInbox(board: Board) {
    return !board.id;
  }

  //event handler
  private onBoardOptionsMenuOpen(event: PointerEvent, board: Board) {
    this.boardOptionsContextMenuOptionsFiltered.splice(0);
    if (!this.isInbox(board)) {
      this.boardOptionsContextMenuOptionsFiltered.push(...this.boardOptionsContextMenuOptions.filter(e => e.canShow(board)));
    }
    if (this.boardOptionsContextMenuOptionsFiltered.length === 0) {
      this.$toasted.info(this.$t('txtNoOptionsAvailable').toString());
      return;
    }
    (this.$refs.boardOptionsContextMenu as VueSimpleContextMenu).showMenu({
      pageX: event.pageX,
      pageY: event.pageY,
    }, board);
  }

  private onBoardOptionsContextMenuClicked(event: { item: Board, option: { id: string } }): void {
    console.log('board', event.item);
    console.log('option', event.option);
    if (event.option.id === 'share') {
      (this.$refs.modalContainer as ModalContainer).openBoardShare(event.item);
    } else if (event.option.id === 'rename') {
      this.editBoardId = event.item.id!;
      this.editBoardName = event.item.name!;
      this.$nextTick(() => (this.$refs.editboardname![0] as HTMLInputElement).focus());
    } else if (event.option.id === 'delete') {
      this.deleteBoard(event.item);
    } else if (event.option.id === 'loggingEnable') {
      this.toggleLogging(event.item, true);
    } else if (event.option.id === 'loggingDisable') {
      this.toggleLogging(event.item, false);
    } else if (event.option.id === 'readConfirmationsEnable') {
      this.toggleReadConfirmations(event.item, true);
    } else if (event.option.id === 'readConfirmationsDisable') {
      this.toggleReadConfirmations(event.item, false);
    } else if (event.option.id === 'lockdownDelay') {
      (this.$refs.modalContainer as ModalContainer).openLockdownDelay(event.item);
    } else if (event.option.id === 'onlyTimeTrackingForOthersEnable') {
      this.toggleOnlyTimeTrackingForOthers(event.item, true);
    } else if (event.option.id === 'onlyTimeTrackingForOthersDisable') {
      this.toggleOnlyTimeTrackingForOthers(event.item, false);
    } else if (event.option.id === 'onlyShowAssignedCardsToOthersEnable') {
      this.toggleOnlyShowAssignedCardsToOthers(event.item, true);
    } else if (event.option.id === 'onlyShowAssignedCardsToOthersDisable') {
      this.toggleOnlyShowAssignedCardsToOthers(event.item, false);
    } else if (event.option.id === 'saveAsTemplate') {
      this.createTemplateFromBoard(event.item.id!);
    } else if (event.option.id === 'changeOwner') {
      (this.$refs.changeBoardOwnerModal as ChangeBoardOwnerModal).open(event.item);
    }
  }

  //watches
  @Watch('boardsToDisplay', { deep: true })//TODO dont do it deep
  private boardsToDisplayChanged(newVal: Board[]) {
    if (this.state.selectedBoard != null && !newVal.includes(this.state.selectedBoard)) {
      this.state.selectedBoard = null;
    }
  }

  //external
  @Watch('state.accounts')
  private accountSelected(): void {
    if (this.state.selectedBoard != null && !this.state.boards.includes(this.state.selectedBoard)) {
      this.state.selectedBoard = null;
    }
  }

  @Watch('state.settings.layout')
  private onLayoutChanged(): void {
    this.calcHeight();
  }

  @Watch('state.selectedViaLog', { deep: true })
  private onSelectedLogChanged(log: SelectedItemDto | null): void {
    if (log?.boardId) {
      var board = this.state.boards.find(b => b.id == log.boardId);
      if (!board)
        return;

      this.state.selectedBoard = null;
      this.state.selectedBoard = board;

      let flow = board.tickets?.find(e => e.id === log.flowId);
      let card = flow?.cards?.find(e => e.id === log.cardId);

      if (flow && card) {
        (this.$refs.modalContainer as ModalContainer).openDetails(board, flow, card);
      }
      return;
    }
  }
}
